import { getAttr, h, transform, withAttr, withClass } from "html-ast-transform";
import { decode } from "html-entities";
import DOMPurify from "isomorphic-dompurify";
import _ from "lodash";
import { DateTime, Duration } from "luxon";
import parse5 from "parse5";

import { getEventDateGroups } from "./event-date-groups";
import { joinNames, joinWithFinalSeparator } from "./strings";

export interface Placeholder {
  readonly id: string;
  readonly label: string;
}

// The set of variables that should be available anywhere
type GuidePlaceholderIDs = {
  "candidate-first-name": string;
  "candidate-last-name": string;
  "candidate-full-name": string;
  "recruiter-first-name": string;
  "recruiter-last-name": string;
  "recruiter-full-name": string;
  "coordinator-first-name": string;
  "coordinator-last-name": string;
  "coordinator-full-name": string;
  "company-name": string;
  "job-role-name": string;
  "greenhouse-job-link": string;
};

export type GuidePlaceholderData = {
  candidateFirstName: string;
  candidateLastName: string;
  candidateFullName: string;
  companyName: string;
  jobRoleName: string;
  recruiterFirstName: string;
  recruiterLastName: string;
  recruiterFullName: string;
  recruiterEmailAddress: string;
  coordinatorFirstName: string;
  coordinatorLastName: string;
  coordinatorFullName: string;
  coordinatorEmailAddress: string;
  guideUrl: string;
  greenhouseJobUrl: string;
};

type ReplaceValueOptions = {
  throwOnMissing: boolean;
};

type Replacements = Partial<GuidePlaceholderIDs>;

export const replaceValues = (
  value: string,
  replacements: Replacements,
  options?: Partial<ReplaceValueOptions>
): string =>
  decode(
    transform(value, {
      replaceTags: {
        span: (node: parse5.Node) => {
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          const tag = getAttr(node, "data-id");
          if (tag) {
            const tagValue = (replacements as Record<string, unknown>)[tag] as
              | string
              | undefined;
            if (tagValue) {
              return h("#text", tagValue);
            }
            if (options?.throwOnMissing) {
              throw new Error(`Missing tag ${tag}`);
            } else {
              return h("#text", "");
            }
          }
          return node;
        },
      },
      trimWhitespace: false,
    })
  );

export const replaceLabels = (
  value: string,
  replacements: Replacements,
  throwOnMissing = false
): string =>
  transform(value, {
    replaceTags: {
      span: (node: parse5.Node) => {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        const tag = getAttr(node, "data-id");
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        const tagLabel = getAttr(node, "data-label");

        if (tag) {
          const tagValue = (replacements as Record<string, unknown>)[tag] as
            | string
            | undefined;
          if (tagValue) {
            const tagValueNode = h(tagValue);
            const childNodes: parse5.Node[] = [];
            let label = tagValue;
            // Support the case where we replacing the label with HTML content, like a link
            if (
              "tagName" in tagValueNode &&
              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
              // @ts-ignore
              (tagValueNode as parse5.DefaultTreeNode).tagName
            ) {
              const firstTextNode = _.find(
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                (tagValueNode as parse5.Node).childNodes,
                {
                  nodeName: "#text",
                }
              );
              label = firstTextNode?.value || tagValue;
              childNodes.push(firstTextNode);
              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
              // @ts-ignore
              const n = withAttr(node, "data-label", label);
              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
              // @ts-ignore
              (n as parse5.Node).childNodes = childNodes;
              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
              // @ts-ignore
              const variable = withClass(
                tagValueNode,
                "atlas-VariableToken variant-read"
              );
              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
              // @ts-ignore
              (variable as parse5.Node).childNodes = [n];
              return variable;
            }
            const child = h("#text", tagValue);
            childNodes.push(child);
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            const n = withAttr(node, "data-label", label);
            const variable = withClass(n, "atlas-VariableToken variant-read");
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            (variable as parse5.Node).childNodes = childNodes;
            return variable;
          }
          if (throwOnMissing) {
            throw new Error(`Missing tag ${tag}`);
          }
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          if (!node.childNodes.length && tagLabel) {
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            node.childNodes.push(h("#text", tagLabel));
          }
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          const variable = withClass(
            node,
            "atlas-VariableToken variant-read is-undefined"
          );
          return variable;
        }
        return node;
      },
    },
    trimWhitespace: false,
  });

type BuildInterviewPanelProps = {
  sortedVisibleEvents: PartialEvent[];
  timezone: string;
  useTimeRanges?: boolean;
  withInterviewers?: boolean;
  withInterviewerFullNames?: boolean;
  html?: boolean;
};

type PartialInterviewer = {
  title: string;
  firstName: string;
  lastName: string;
};

type PartialEvent = {
  startTime: string;
  endTime: string;
  title: string;
  hidden: boolean;
  interviewers: PartialInterviewer[];
};

export const buildInterviewPanelString = ({
  sortedVisibleEvents,
  timezone,
  useTimeRanges = true,
  withInterviewers = false,
  withInterviewerFullNames = false,
  html = true,
}: BuildInterviewPanelProps): string => {
  const showHidden = true;
  const dateGroups = getEventDateGroups<PartialEvent>(
    sortedVisibleEvents,
    showHidden
  );
  const LINE_OPEN = html ? "<span>" : "";
  const LINE_CLOSE = html ? "</span>" : "";
  const LINE_BREAK = html ? "<br>" : "\n";
  const MIDDOT = html ? "  &middot;" : "•";
  const SPACE = html ? "&nbsp;" : " ";

  let res = "";
  if (dateGroups.length) {
    res = dateGroups.reduce((acc, next, idx) => {
      const { startTime: dateGroupStartTime, events: dateGroupEvents } = next;

      let dayEventsStr = "";
      dateGroupEvents.forEach((eventOrBreak, eventIdx) => {
        if ("lengthMinutes" in eventOrBreak) {
          const breakTime = Duration.fromObject({
            minutes: eventOrBreak.lengthMinutes,
          }).toFormat("h [hr] m [min]");
          const lineBreak =
            eventIdx === dateGroupEvents.length - 1 ? "" : LINE_BREAK;
          dayEventsStr = `${dayEventsStr}${LINE_OPEN}${SPACE.repeat(
            6
          )}~ ${breakTime} break ~${LINE_CLOSE}${lineBreak}`;
          return;
        }
        const event = eventOrBreak;

        const sanitizedTitle = DOMPurify.sanitize(event.title, {
          ALLOWED_TAGS: [],
        });

        let interviewersStr = "";
        if (withInterviewers && event.interviewers.length) {
          interviewersStr = ` with ${_(event.interviewers)
            .map(({ firstName, lastName }) => {
              if (withInterviewerFullNames) {
                return joinNames(firstName, lastName);
              }
              return firstName;
            })
            .sort()
            .thru(joinWithFinalSeparator)
            .value()}`;
        }

        let interviewTime;
        if (useTimeRanges) {
          interviewTime = `${DateTime.fromISO(event.startTime, {
            zone: timezone,
          }).toFormat("h:mm")} - ${DateTime.fromISO(event.endTime, {
            zone: timezone,
          }).toFormat("h:mm a z")}: `;
        } else {
          interviewTime = `${DateTime.fromISO(event.startTime, {
            zone: timezone,
          }).toFormat("h:mm a z")} - `;
        }

        const lineBreak =
          eventIdx === dateGroupEvents.length - 1 ? "" : LINE_BREAK;
        dayEventsStr = `${dayEventsStr}${LINE_OPEN}${MIDDOT}${interviewTime}${sanitizedTitle}${interviewersStr}${LINE_CLOSE}${lineBreak}`;
      });
      const dayHeader = DateTime.fromISO(dateGroupStartTime, {
        zone: timezone,
      }).toFormat("EEEE, MMM d");

      const lineBreak =
        idx === dateGroups.length - 1 ? "" : `${LINE_BREAK}${LINE_BREAK}`;
      const dayStr = `${LINE_OPEN}${dayHeader}${LINE_CLOSE}${LINE_BREAK}${dayEventsStr}${lineBreak}`;

      return `${acc}${dayStr}`;
    }, "");
  }

  return res;
};
